import React, { useEffect, useRef, useState } from "react";
import styles from "../../styles/components/RecentMessages.module.css";
import { ReactComponent as VectorArrowIcon } from "../../assets/images/vector_arrow.svg";

const CORNER = 24;
const CORNER_CP = 10.7452;

const NOTCH_SCALE = 1.05;
const NX = (offset) => offset * NOTCH_SCALE;
const NY = (value) => value * NOTCH_SCALE;

function buildCardPath(width, height) {
  const w = width;
  const h = height;
  return [
    `M0 ${CORNER}`,
    `V${h - CORNER}`,
    `C0 ${h - CORNER_CP} ${CORNER_CP} ${h} ${CORNER} ${h}`,
    `H${w - CORNER}`,
    `C${w - CORNER_CP} ${h} ${w} ${h - CORNER_CP} ${w} ${h - CORNER}`,
    `V${NY(77.4264)}`,
    `C${w} ${NY(63.6205)} ${w - NX(12.039)} ${NY(52.9025)} ${w - NX(25.752)} ${NY(54.5)}`,
    `C${w - NX(39.465)} ${NY(56.0975)} ${w - NX(51.504)} ${NY(45.3795)} ${w - NX(51.504)} ${NY(31.5736)}`,
    `V${NY(24)}`,
    `C${w - NX(51.504)} ${NY(10.7452)} ${w - NX(62.249)} 0 ${w - NX(75.504)} 0`,
    `H${CORNER}`,
    `C${CORNER_CP} 0 0 ${CORNER_CP} 0 ${CORNER}`,
    `Z`,
  ].join(" ");
}

function getCurrentTheme() {
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

function useTheme() {
  const [theme, setTheme] = useState(getCurrentTheme);
  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(getCurrentTheme()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return theme;
}

function useElementSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [ref, size];
}

function CardSurface({ theme }) {
  const [ref, { width, height }] = useElementSize();
  const fill = theme === "dark" ? "#232323" : "#ffffff";

  return (
    <div ref={ref} className={styles.cardSurface}>
      {width > 0 && height > 0 && (
        <svg
          className={styles.cardSurfaceSvg}
          width={width}
          height={height}
          viewBox={`0 0 ${width} ${height}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d={buildCardPath(width, height)} fill={fill} fillOpacity={0.8} />
        </svg>
      )}
    </div>
  );
}

const RELATIVE_TIME_UNITS = [
  { limitSeconds: 60,       divisor: 1,        singular: "segundo", plural: "segundos" },
  { limitSeconds: 3600,     divisor: 60,       singular: "minuto",  plural: "minutos"  },
  { limitSeconds: 86400,    divisor: 3600,     singular: "hora",    plural: "horas"    },
  { limitSeconds: 2592000,  divisor: 86400,    singular: "día",     plural: "días"     },
  { limitSeconds: 31536000, divisor: 2592000,  singular: "mes",     plural: "meses"    },
  { limitSeconds: Infinity, divisor: 31536000, singular: "año",     plural: "años"     },
];

function formatRelativeTime(isoTimestamp) {
  if (!isoTimestamp) return "";
  const elapsedSeconds = Math.floor((Date.now() - new Date(isoTimestamp).getTime()) / 1000);
  if (Number.isNaN(elapsedSeconds)) return "";
  if (elapsedSeconds < 30) return "Recién";

  const unit = RELATIVE_TIME_UNITS.find((candidate) => elapsedSeconds < candidate.limitSeconds);
  const amount = Math.floor(elapsedSeconds / unit.divisor);
  return `Hace ${amount} ${amount === 1 ? unit.singular : unit.plural}`;
}

function MessageCard({ message, theme, onOpenLead, onOpenConversation }) {
  const { lead_id, lead_name, contact_name, account, preview, created_at } = message;
  const badgeColor = account?.color || "var(--surface-accent, #359fbb)";

  return (
    <div
      className={styles.card}
      role="button"
      tabIndex={0}
      onClick={() => onOpenLead(message)}
      onKeyDown={(keyEvent) => {
        if (keyEvent.key === "Enter" || keyEvent.key === " ") {
          keyEvent.preventDefault();
          onOpenLead(message);
        }
      }}
    >
      <CardSurface theme={theme} />

      <div className={styles.cardContent}>
        <div className={styles.cardTopRow}>
          <span className={styles.cardTitle}>
            Nuevo mensaje de <strong>{contact_name}</strong>
          </span>
          {account?.name && (
            <span className={styles.accountBadge} style={{ backgroundColor: badgeColor }}>
              {account.name}
            </span>
          )}
        </div>

        <div className={styles.cardLead}>
          <span className={styles.leadName}>{lead_name}</span>
          {lead_id != null && <span className={styles.leadId}>#{lead_id}</span>}
        </div>

        <p className={styles.cardPreview}>{preview}</p>

        <span className={styles.cardTime}>{formatRelativeTime(created_at)}</span>
      </div>

      <button
        type="button"
        className={styles.modalButton}
        aria-label="Abrir mensaje"
        onClick={(clickEvent) => {
          clickEvent.stopPropagation();
          onOpenConversation(message);
        }}
      >
        <VectorArrowIcon className={styles.modalButtonIcon} />
      </button>
    </div>
  );
}

export default function RecentMessages({ messages, onOpenLead, onOpenConversation }) {
  const theme = useTheme();
  if (!messages?.length) return null;

  return (
    <section className={styles.panel}>
      <h2 className={styles.panelTitle}>Nuevos Mensajes</h2>
      <div className={styles.grid}>
        {messages.map((message) => (
          <MessageCard
            key={message.id}
            message={message}
            theme={theme}
            onOpenLead={onOpenLead}
            onOpenConversation={onOpenConversation}
          />
        ))}
      </div>
    </section>
  );
}
